/**
 * @file sanitizer.js
 * @description Security layer using DOMPurify for XSS prevention
 * @version 1.0.0
 * @requires dompurify
 */

import DOMPurify from 'dompurify';

class Sanitizer {
  /**
   * Sanitize HTML content to prevent XSS attacks
   * @param {string} dirty - Unsanitized HTML string
   * @returns {string} - Sanitized HTML safe for rendering
   */
  static sanitizeHTML(dirty) {
    if (!dirty || typeof dirty !== 'string') {
      return '';
    }

    return DOMPurify.sanitize(dirty, {
      ALLOWED_TAGS: [
        'b', 'i', 'em', 'strong', 'a', 'p', 'br', 'span', 'div',
        'pre', 'code', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
        'ul', 'ol', 'li', 'blockquote', 'hr'
      ],
      ALLOWED_ATTR: ['href', 'class', 'id', 'target', 'rel'],
      ALLOW_DATA_ATTR: false,
      SAFE_FOR_TEMPLATES: true
    });
  }

  /**
   * Sanitize terminal command input to prevent command injection
   * @param {string} input - User terminal input
   * @returns {string} - Sanitized command string
   */
  static sanitizeCommand(input) {
    if (!input || typeof input !== 'string') {
      return '';
    }

    // Remove dangerous shell characters while preserving useful ones
    // Allow: alphanumeric, spaces, hyphens, underscores, dots, forward slashes
    // Block: semicolons, pipes, backticks, dollar signs, parentheses, etc.
    return input
      .replace(/[;&|`$(){}[\]<>]/g, '') // Remove shell metacharacters
      .replace(/\\/g, '')              // Remove backslash escaping
      .trim();
  }

  /**
   * Sanitize file path to prevent directory traversal attacks
   * @param {string} path - File path from user input
   * @returns {string} - Sanitized path
   */
  static sanitizeFilePath(path) {
    if (!path || typeof path !== 'string') {
      return '';
    }

    // Remove parent directory references and multiple slashes
    return path
      .replace(/\.\./g, '')  // Remove ../
      .replace(/\/+/g, '/')   // Normalize multiple slashes
      .replace(/^\/+/, '/')   // Ensure single leading slash
      .trim();
  }

  /**
   * Sanitize user-generated text (descriptions, comments)
   * @param {string} text - User-generated text
   * @returns {string} - Sanitized text (plain text, no HTML)
   */
  static sanitizeText(text) {
    if (!text || typeof text !== 'string') {
      return '';
    }

    // Strip all HTML tags, only allow plain text
    return DOMPurify.sanitize(text, {
      ALLOWED_TAGS: [],
      KEEP_CONTENT: true
    });
  }

  /**
   * Check if input contains potential XSS patterns
   * @param {string} input - Input to check
   * @returns {boolean} - True if suspicious patterns detected
   */
  static containsXSS(input) {
    if (!input || typeof input !== 'string') {
      return false;
    }

    const xssPatterns = [
      /<script/i,
      /javascript:/i,
      /on\w+\s*=/i,  // Event handlers like onclick=
      /<iframe/i,
      /<object/i,
      /<embed/i
    ];

    return xssPatterns.some(pattern => pattern.test(input));
  }
}

export default Sanitizer;
